import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { devisAttachmentsApiService, DevisAttachmentItem } from '../../services/DevisAttachmentsApiService';

type Props = {
  devisId: string;
};

const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_BYTES = 50 * 1024 * 1024;

const ALLOWED_EXTENSIONS = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'webp',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'odt',
  'ods',
  'odp',
  'odg',
];

const humanSize = (bytes: number) => {
  if (!bytes) return '0 Mo';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${mb.toFixed(1)} Mo`;
};

const errorMessages: Record<string, string> = {
  FILE_TOO_LARGE: 'Fichier trop volumineux (max 10 Mo).',
  MAX_FILES_REACHED: 'Nombre maximal de fichiers atteint (5).',
  MAX_TOTAL_SIZE_REACHED: 'Taille cumulée dépassée (50 Mo).',
  UNSUPPORTED_FILE_TYPE: 'Type de fichier non autorisé.',
  NOT_FOUND: 'Élément introuvable.',
};

export const DevisAttachmentsSection: React.FC<Props> = ({ devisId }) => {
  const [items, setItems] = useState<DevisAttachmentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalBytes = useMemo(() => items.reduce((acc, item) => acc + (item.size_bytes || 0), 0), [items]);
  const count = items.length;

  const disabledUpload = count >= MAX_FILES || totalBytes >= MAX_TOTAL_BYTES;

  const fetchItems = useCallback(async () => {
    if (!devisId) return;
    try {
      setLoading(true);
      const res = await devisAttachmentsApiService.list(devisId, 100, 0);
      setItems(res.items || []);
    } catch (e: any) {
      setError(e?.message || 'Erreur lors du chargement des pièces jointes.');
    } finally {
      setLoading(false);
    }
  }, [devisId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleErrorCode = (code?: string, fallback?: string) => {
    if (code && errorMessages[code]) {
      setError(errorMessages[code]);
    } else {
      setError(fallback || 'Erreur lors du transfert du fichier.');
    }
  };

  const validateFile = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return errorMessages.UNSUPPORTED_FILE_TYPE;
    }
    if (file.size > MAX_FILE_BYTES) {
      return errorMessages.FILE_TOO_LARGE;
    }
    if (count + 1 > MAX_FILES) {
      return errorMessages.MAX_FILES_REACHED;
    }
    if (totalBytes + file.size > MAX_TOTAL_BYTES) {
      return errorMessages.MAX_TOTAL_SIZE_REACHED;
    }
    return null;
  };

  const uploadFiles = async (fileList: FileList | null) => {
    if (!fileList || !fileList.length) return;
    setError(null);
    const files = Array.from(fileList);

    for (const file of files) {
      const err = validateFile(file);
      if (err) {
        setError(err);
        return;
      }
    }

    try {
      setUploading(true);
      for (const file of files) {
        await devisAttachmentsApiService.upload(devisId, file);
      }
      await fetchItems();
    } catch (e: any) {
      const code = e?.code || e?.statusCode || e?.status;
      if (e?.message && typeof e.message === 'string' && e.message.startsWith('{')) {
        try {
          const parsed = JSON.parse(e.message);
          handleErrorCode(parsed.code, parsed.error);
          return;
        } catch {
          /* ignore */
        }
      }
      handleErrorCode(code, e?.message);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (evt: React.DragEvent<HTMLDivElement>) => {
    evt.preventDefault();
    if (disabledUpload) return;
    uploadFiles(evt.dataTransfer.files);
  };

  const onFileInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    uploadFiles(evt.target.files);
    evt.target.value = '';
  };

  const onDelete = async (attachmentId: number) => {
    if (!window.confirm('Supprimer ce fichier ?')) return;
    try {
      await devisAttachmentsApiService.delete(devisId, attachmentId);
      await fetchItems();
    } catch (e: any) {
      handleErrorCode(e?.code, e?.message || 'Erreur lors de la suppression.');
    }
  };

  const onDownload = async (attachmentId: number, filename: string) => {
    try {
      setError(null);
      await devisAttachmentsApiService.download(devisId, attachmentId, filename);
    } catch (e: any) {
      handleErrorCode(e?.code, e?.message || 'Erreur lors du téléchargement.');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Pièces jointes</h3>
          <p className="text-sm text-gray-600">Max 5 fichiers — 10 Mo/fichier — 50 Mo au total</p>
        </div>
        <div className="text-sm text-gray-700">
          <div>{count}/{MAX_FILES} fichiers</div>
          <div>{humanSize(totalBytes)} / {humanSize(MAX_TOTAL_BYTES)}</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <span className="block text-sm">{error}</span>
          <button
            type="button"
            className="absolute top-2 right-2 text-red-500 hover:text-red-700"
            onClick={() => setError(null)}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>
      )}

      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition ${
          disabledUpload ? 'border-gray-200 bg-gray-50 text-gray-400' : 'border-gray-300 hover:border-primary-400'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={onDrop}
      >
        <p className="text-sm text-gray-700 mb-2">
          Glissez-déposez vos fichiers ici ou
        </p>
        <label className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded cursor-pointer hover:bg-primary-700 transition">
          Ajouter un fichier
          <input
            type="file"
            className="hidden"
            onChange={onFileInputChange}
            multiple
            disabled={disabledUpload || uploading}
            accept={ALLOWED_EXTENSIONS.map((ext) => '.' + ext).join(',')}
          />
        </label>
        {disabledUpload && (
          <p className="text-xs text-gray-500 mt-2">
            Limite atteinte : supprimez un fichier pour en ajouter un nouveau.
          </p>
        )}
      </div>

      <div className="divide-y divide-gray-200">
        {loading && <div className="text-sm text-gray-500 py-2">Chargement des pièces jointes...</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm text-gray-500 py-2">Aucune pièce jointe.</div>
        )}
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between py-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">{item.original_filename || `Fichier ${item.id}`}</div>
              <div className="text-xs text-gray-600">
                {humanSize(item.size_bytes || 0)} • {item.created_at ? new Date(item.created_at).toLocaleString('fr-FR') : 'Date inconnue'}
              </div>
            </div>
            <div className="flex items-center space-x-2 ml-4">
              <button
                type="button"
                className="text-primary-600 hover:text-primary-800 text-sm cursor-pointer"
                onClick={() => onDownload(item.id, item.original_filename || `fichier-${item.id}`)}
              >
                Télécharger
              </button>
              <button
                type="button"
                className="text-red-600 hover:text-red-800 text-sm"
                onClick={() => onDelete(item.id)}
              >
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DevisAttachmentsSection;
